import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, TrendingDown, DollarSign, AlertTriangle, BarChart3, PieChart, Activity, Calendar, Wallet, X, ChevronRight, GraduationCap, BookOpen, Target, Zap } from 'lucide-react';

const ROICalculator = () => {
  // Active Tab
  const [activeTab, setActiveTab] = useState('course'); // 'course' or 'trading'
  
  // Course Tab States
  const [coursePrice, setCoursePrice] = useState(13500); // בשקלים
  const [learningMonths, setLearningMonths] = useState(6); // חודשי למידה
  const exchangeRate = 3.5; // שקל לדולר
  
  // Course Trading Parameters - same as regular trading
  const [courseInvestment, setCourseInvestment] = useState(500); // השקעה ראשונית בדולרים אחרי הקורס
  const [courseRiskPercent, setCourseRiskPercent] = useState(2);
  const [courseIsFixedRisk, setCourseIsFixedRisk] = useState(false);
  const [courseFixedRiskAmount, setCourseFixedRiskAmount] = useState(10);
  const [courseRiskRewardRatio, setCourseRiskRewardRatio] = useState(3);
  const [courseWinRate, setCourseWinRate] = useState(30);
  const [courseMonthlyTrades, setCourseMonthlyTrades] = useState(20);
  const [courseYearsToCalculate, setCourseYearsToCalculate] = useState(3);
  
  // Course Results
  const [courseROI, setCourseROI] = useState(0);
  const [breakEvenMonth, setBreakEvenMonth] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [courseChartData, setCourseChartData] = useState([]);
  const [courseMonthlyProfit, setCourseMonthlyProfit] = useState(0);
  const [courseFinalPortfolio, setCourseFinalPortfolio] = useState(0);
  
  // Trading Tab States (Original states)
  const [isFundedAccount, setIsFundedAccount] = useState(false);
  const [isFixedRisk, setIsFixedRisk] = useState(false);
  const [investment, setInvestment] = useState(200);
  const [riskPercent, setRiskPercent] = useState(10);
  const [fixedRiskAmount, setFixedRiskAmount] = useState(20);
  const [riskRewardRatio, setRiskRewardRatio] = useState(5);
  const [winRate, setWinRate] = useState(25);
  const [monthlyTrades, setMonthlyTrades] = useState(10);
  const [yearsToCalculate, setYearsToCalculate] = useState(1);
  
  // Modal state
  const [selectedMonthDetails, setSelectedMonthDetails] = useState(null);
  
  // נתוני FTMO
  const ftmoAccounts = {
    10000: { cost: 85, dailyMax: 500, totalMax: 1000 },
    25000: { cost: 145, dailyMax: 1250, totalMax: 2500 },
    50000: { cost: 245, dailyMax: 2500, totalMax: 5000 },
    100000: { cost: 345, dailyMax: 5000, totalMax: 10000 },
    200000: { cost: 645, dailyMax: 10000, totalMax: 20000 }
  };
  
  const [selectedFTMO, setSelectedFTMO] = useState(10000);
  
  // חישובים אוטומטיים
  const [riskAmountDollar, setRiskAmountDollar] = useState(0);
  const [profitPerTrade, setProfitPerTrade] = useState(0);
  const [winningTrades, setWinningTrades] = useState(0);
  const [losingTrades, setLosingTrades] = useState(0);
  
  // תוצאות
  const [monthlyProfit, setMonthlyProfit] = useState(0);
  const [monthlyROI, setMonthlyROI] = useState(0);
  const [yearlyROI, setYearlyROI] = useState(0);
  const [maxDD, setMaxDD] = useState(0);
  const [monthlyPortfolio, setMonthlyPortfolio] = useState(0);
  const [finalPortfolio, setFinalPortfolio] = useState(0);

  // נתונים לגרפים
  const [chartData, setChartData] = useState([]);
  const [periodData, setPeriodData] = useState([]);
  
  // Handle input changes with proper formatting
  const handleNumberInput = (value, setter) => {
    const numValue = value === '' ? '' : Number(value);
    setter(numValue);
  };
  
  // Course calculations
  useEffect(() => {
    if (activeTab === 'course') {
      // המרת מחיר הקורס לדולרים
      const coursePriceInDollars = coursePrice / exchangeRate;
      
      // חישוב רווח חודשי מהמסחר (אותו חישוב כמו במחשבון הרגיל)
      let initialRiskDollar;
      if (courseIsFixedRisk) {
        initialRiskDollar = courseFixedRiskAmount;
      } else {
        initialRiskDollar = courseInvestment * (courseRiskPercent / 100);
      }
      
      const initialProfitPerTrade = initialRiskDollar * courseRiskRewardRatio;
      const winTrades = (courseWinRate / 100) * courseMonthlyTrades;
      const loseTrades = courseMonthlyTrades - winTrades;
      const initialMonthlyProfit = (winTrades * initialProfitPerTrade) - (loseTrades * initialRiskDollar);
      
      setCourseMonthlyProfit(initialMonthlyProfit);
      
      // יצירת נתונים לגרף
      const data = [];
      let cumulativeEarnings = -coursePriceInDollars; // מתחילים ממינוס עלות הקורס
      let currentPortfolio = 0; // תיק המסחר מתחיל מ-0
      let breakEven = 0;
      
      const monthsToCalculate = courseYearsToCalculate * 12;
      
      for (let month = 0; month <= monthsToCalculate; month++) {
        // תקופת למידה - אין מסחר
        if (month < learningMonths) {
          data.push({
            month,
            earnings: cumulativeEarnings,
            portfolio: 0,
            monthlyProfit: 0,
            status: 'learning'
          });
        }
        // התחלת מסחר אחרי הלמידה
        else {
          // בחודש הראשון של המסחר מוסיפים את ההשקעה הראשונית
          if (month === learningMonths) {
            currentPortfolio = courseInvestment;
          }
          
          let currentMonthlyProfit;
          
          if (courseIsFixedRisk) {
            // סיכון קבוע - רווח קבוע כל חודש
            currentMonthlyProfit = initialMonthlyProfit;
          } else {
            // סיכון באחוזים - ריבית דריבית
            const currentRiskDollar = currentPortfolio * (courseRiskPercent / 100);
            const currentProfitPerTrade = currentRiskDollar * courseRiskRewardRatio;
            currentMonthlyProfit = (winTrades * currentProfitPerTrade) - (loseTrades * currentRiskDollar);
          }
          
          currentPortfolio += currentMonthlyProfit;
          cumulativeEarnings += currentMonthlyProfit;
          
          data.push({
            month,
            earnings: cumulativeEarnings,
            portfolio: currentPortfolio,
            monthlyProfit: currentMonthlyProfit,
            status: cumulativeEarnings < 0 ? 'recovering' : 'profitable'
          });
          
          if (cumulativeEarnings >= 0 && breakEven === 0) {
            breakEven = month;
          }
        }
      }
      
      setCourseChartData(data);
      setBreakEvenMonth(breakEven);
      setTotalEarnings(cumulativeEarnings);
      setCourseFinalPortfolio(currentPortfolio);
      
      // חישוב ROI כולל על ההשקעה בקורס
      const totalInvestment = coursePriceInDollars + courseInvestment;
      const totalProfit = cumulativeEarnings + coursePriceInDollars;
      setCourseROI(totalInvestment > 0 ? (totalProfit / totalInvestment) * 100 : 0);
    }
  }, [activeTab, coursePrice, learningMonths, courseInvestment, courseRiskPercent, courseIsFixedRisk, courseFixedRiskAmount, courseRiskRewardRatio, courseWinRate, courseMonthlyTrades, courseYearsToCalculate]);
  
  // עדכון ההשקעה כשעוברים לתיק ממומן
  useEffect(() => {
    if (isFundedAccount) {
      setInvestment(selectedFTMO);
      // התאמת הסיכון למגבלות FTMO
      const maxRiskPercent = (ftmoAccounts[selectedFTMO].dailyMax / selectedFTMO) * 100;
      if (!isFixedRisk && riskPercent > maxRiskPercent) {
        setRiskPercent(Math.floor(maxRiskPercent));
      }
    }
  }, [isFundedAccount, selectedFTMO]);
  
  // Generate trade simulation for a month
  const generateMonthTrades = (monthName, winTrades, loseTrades, riskDollar, profitPerTrade) => {
    const trades = [];
    const totalTrades = Math.round(winTrades + loseTrades);
    const actualWins = Math.round(winTrades);
    
    // Generate random trades
    for (let i = 0; i < totalTrades; i++) {
      const isWin = i < actualWins;
      trades.push({
        id: i + 1,
        type: isWin ? 'win' : 'loss',
        amount: isWin ? profitPerTrade : -riskDollar,
        date: `${Math.floor(Math.random() * 28) + 1}/${monthName}`
      });
    }
    
    // Shuffle trades to make it realistic
    trades.sort(() => Math.random() - 0.5);
    
    return trades;
  };
  
  // Original trading calculations
  useEffect(() => {
    if (activeTab === 'trading') {
      // המרת ערכים ריקים ל-0 לחישובים
      const safeInvestment = investment === '' ? 0 : investment;
      const safeRiskPercent = riskPercent === '' ? 0 : riskPercent;
      const safeFixedRiskAmount = fixedRiskAmount === '' ? 0 : fixedRiskAmount;
      const safeRiskRewardRatio = riskRewardRatio === '' ? 0 : riskRewardRatio;
      const safeWinRate = winRate === '' ? 0 : winRate;
      const safeMonthlyTrades = monthlyTrades === '' ? 0 : monthlyTrades;
      
      // חישוב יחידת סיכון דולרית
      let riskDollar;
      if (isFixedRisk) {
        riskDollar = safeFixedRiskAmount;
      } else {
        riskDollar = safeInvestment * (safeRiskPercent / 100);
      }
      
      // בדיקה למגבלות FTMO
      if (isFundedAccount) {
        const ftmo = ftmoAccounts[selectedFTMO];
        if (riskDollar > ftmo.dailyMax) {
          riskDollar = ftmo.dailyMax;
        }
      }
      
      setRiskAmountDollar(riskDollar);
      
      // חישוב רווח צפוי לעסקה
      const expectedProfitPerTrade = riskDollar * safeRiskRewardRatio;
      setProfitPerTrade(expectedProfitPerTrade);
      
      // חישוב עסקאות מרוויחות ומפסידות מתוך WIN RATE
      const winTrades = (safeWinRate / 100) * safeMonthlyTrades;
      const loseTrades = safeMonthlyTrades - winTrades;
      setWinningTrades(winTrades);
      setLosingTrades(loseTrades);
      
      // חישוב רווח חודשי
      const monthlyProfitCalc = (winTrades * expectedProfitPerTrade) - (loseTrades * riskDollar);
      setMonthlyProfit(monthlyProfitCalc);
      
      // חישוב ROI חודשי
      const monthlyROICalc = safeInvestment > 0 ? (monthlyProfitCalc / safeInvestment) * 100 : 0;
      setMonthlyROI(monthlyROICalc);
      
      // חישוב MAX DRAWDOWN
      const maxDrawdown = riskDollar * loseTrades;
      setMaxDD(maxDrawdown);
      
      // חישוב תיק חודשי
      const monthlyPortfolioCalc = safeInvestment + monthlyProfitCalc;
      setMonthlyPortfolio(monthlyPortfolioCalc);
      
      // חישוב תיק שנתי - תלוי בסוג הסיכון
      let yearlyPortfolioCalc;
      let yearlyROICalc;
      let data = [];
      
      if (isFixedRisk) {
        // בסיכון קבוע - צמיחה לינארית (אין ריבית דריבית)
        const totalMonthlyProfit = monthlyProfitCalc * 12 * yearsToCalculate;
        yearlyPortfolioCalc = safeInvestment + totalMonthlyProfit;
        
        // ROI שנתי בסיכון קבוע
        yearlyROICalc = safeInvestment > 0 ? ((monthlyProfitCalc * 12) / safeInvestment) * 100 : 0;
        
        // יצירת נתונים לגרף צמיחה - לינארי
        const monthsToShow = Math.min(yearsToCalculate * 12, 120);
        for (let i = 0; i <= monthsToShow; i++) {
          data.push(safeInvestment + (monthlyProfitCalc * i));
        }
      } else {
        // בסיכון באחוזים - צמיחה עם ריבית דריבית
        const monthlyGrowthRate = safeInvestment > 0 ? 1 + (monthlyProfitCalc / safeInvestment) : 1;
        yearlyPortfolioCalc = safeInvestment * Math.pow(monthlyGrowthRate, 12 * yearsToCalculate);
        
        // ROI שנתי עם ריבית דריבית
        yearlyROICalc = ((Math.pow(monthlyGrowthRate, 12) - 1) * 100);
        
        // יצירת נתונים לגרף צמיחה - אקספוננציאלי
        let currentValue = safeInvestment;
        const monthsToShow = Math.min(yearsToCalculate * 12, 120);
        
        for (let i = 0; i <= monthsToShow; i++) {
          data.push(currentValue);
          currentValue *= monthlyGrowthRate;
        }
      }
      
      setFinalPortfolio(yearlyPortfolioCalc);
      setYearlyROI(yearlyROICalc);
      setChartData(data.length > 0 ? data : [0]);
      
      // יצירת נתונים לגרף תקופתי
      const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
      const quarterNames = ['רבעון 1', 'רבעון 2', 'רבעון 3', 'רבעון 4'];
      
      let newPeriodData = [];
      
      if (yearsToCalculate <= 2) {
        // תצוגה חודשית
        const monthsToGenerate = Math.min(yearsToCalculate * 12, 24);
        for (let i = 0; i < monthsToGenerate; i++) {
          let monthProfit;
          let actualWinTrades = winTrades;
          let actualLoseTrades = loseTrades;
          
          if (isFixedRisk) {
            // בסיכון קבוע - רווח קבוע כל חודש
            monthProfit = monthlyProfitCalc;
          } else {
            // בסיכון באחוזים - שונות ריאלית
            const variance = (Math.random() - 0.5) * 0.3;
            actualWinTrades = Math.max(0, winTrades * (1 + variance));
            actualLoseTrades = Math.max(0, loseTrades * (1 - variance));
            monthProfit = (actualWinTrades * expectedProfitPerTrade) - (actualLoseTrades * riskDollar);
          }
          
          newPeriodData.push({
            period: monthNames[i % 12],
            value: monthProfit,
            positive: monthProfit > 0,
            type: 'month',
            trades: generateMonthTrades(monthNames[i % 12], actualWinTrades, actualLoseTrades, riskDollar, expectedProfitPerTrade)
          });
        }
      } else if (yearsToCalculate === 3) {
        // תצוגה רבעונית
        for (let i = 0; i < 12; i++) {
          let quarterlyProfit;
          
          if (isFixedRisk) {
            // בסיכון קבוע - רווח קבוע לרבעון
            quarterlyProfit = monthlyProfitCalc * 3;
          } else {
            // בסיכון באחוזים - שונות ריאלית
            const variance = (Math.random() - 0.5) * 0.2;
            quarterlyProfit = monthlyProfitCalc * 3 * (1 + variance);
          }
          
          newPeriodData.push({
            period: quarterNames[i % 4] + ` ${Math.floor(i / 4) + 1}`,
            value: quarterlyProfit,
            positive: quarterlyProfit > 0,
            type: 'quarter'
          });
        }
      } else {
        // תצוגה שנתית
        for (let i = 1; i <= yearsToCalculate; i++) {
          let yearlyProfit;
          
          if (isFixedRisk) {
            // בסיכון קבוע - רווח קבוע לשנה
            yearlyProfit = monthlyProfitCalc * 12;
          } else {
            // בסיכון באחוזים - שונות ריאלית
            const variance = (Math.random() - 0.5) * 0.15;
            yearlyProfit = monthlyProfitCalc * 12 * (1 + variance);
          }
          
          newPeriodData.push({
            period: `שנה ${i}`,
            value: yearlyProfit,
            positive: yearlyProfit > 0,
            type: 'year'
          });
        }
      }
      
      setPeriodData(newPeriodData);
    }
  }, [activeTab, investment, riskPercent, fixedRiskAmount, isFixedRisk, riskRewardRatio, winRate, monthlyTrades, yearsToCalculate, isFundedAccount, selectedFTMO]);

  // פונקציה לפורמט מספרים
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return Math.round(num).toLocaleString();
  };

  return (
    <div className="bg-gray-900 p-6 rounded-2xl" dir="rtl">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2 text-white">דארק קריפטו - מחשבון ROI</h2>
        <p className="text-gray-400">חשב את הרווחיות הפוטנציאלית שלך במסחר</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('course')}
          className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'course'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          <GraduationCap size={20} />
          מסלול דארק
        </button>
        <button
          onClick={() => setActiveTab('trading')}
          className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'trading'
              ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          <TrendingUp size={20} />
          מחשבון מסחר
        </button>
      </div>

      {/* Course ROI Tab */}
      {activeTab === 'course' && (
        <div>
          {/* Course Info Card */}
          <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-600/30 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="bg-purple-600/20 p-3 rounded-lg">
                <BookOpen className="text-purple-400" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">מסלול דארק - קורס מסחר מקצועי</h3>
                <p className="text-gray-300 mb-4">
                  לומדים את השיטה • מקבלים כלים מקצועיים • מתחילים לסחור • בונים תיק עם ריבית דריבית
                </p>
                <div className="bg-gray-800/50 p-3 rounded-lg inline-block">
                  <p className="text-gray-400 text-sm">המרת מחיר הקורס:</p>
                  <p className="text-white font-bold text-lg">
                    ₪{coursePrice.toLocaleString()} = ${(coursePrice / exchangeRate).toFixed(0)}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">שער חליפין: $1 = ₪3.5</p>
                </div>
              </div>
            </div>
          </div>

          {/* Course Parameters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-800 p-4 rounded-xl">
              <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                <DollarSign size={16} />
                מחיר הקורס (₪)
              </label>
              <input
                type="number"
                value={coursePrice === '' ? '' : coursePrice}
                onChange={(e) => handleNumberInput(e.target.value, setCoursePrice)}
                className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">ברירת מחדל: ₪13,500</p>
            </div>

            <div className="bg-gray-800 p-4 rounded-xl">
              <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                <Calendar size={16} />
                חודשי למידה
              </label>
              <input
                type="number"
                value={learningMonths === '' ? '' : learningMonths}
                onChange={(e) => handleNumberInput(e.target.value, setLearningMonths)}
                className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
                min="1"
                max="12"
              />
              <p className="text-xs text-gray-500 mt-1">תקופת הלמידה לפני תחילת מסחר</p>
            </div>

            <div className="bg-gray-800 p-4 rounded-xl">
              <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                <Wallet size={16} />
                השקעה ראשונית למסחר ($)
              </label>
              <input
                type="number"
                value={courseInvestment === '' ? '' : courseInvestment}
                onChange={(e) => handleNumberInput(e.target.value, setCourseInvestment)}
                className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">הסכום שתתחיל איתו אחרי הקורס</p>
            </div>

            <div className="bg-gray-800 p-4 rounded-xl">
              <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                <Calendar size={16} />
                תקופת תחזית
              </label>
              <div className="grid grid-cols-3 gap-1">
                {[1, 2, 3].map((year) => (
                  <button
                    key={year}
                    onClick={() => setCourseYearsToCalculate(year)}
                    className={`py-2 px-2 rounded-lg transition-all text-sm ${
                      courseYearsToCalculate === year
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {year === 1 ? 'שנה' : `${year} שנים`}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-1 mt-1">
                {[5, 7, 10].map((year) => (
                  <button
                    key={year}
                    onClick={() => setCourseYearsToCalculate(year)}
                    className={`py-2 px-2 rounded-lg transition-all text-sm ${
                      courseYearsToCalculate === year
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {`${year} שנים`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Risk Type Toggle for Course */}
          <div className="bg-gray-800 p-4 rounded-xl mb-6">
            <div className="flex flex-col items-center space-y-3">
              <p className="text-sm text-gray-400">שיטת סיכון</p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setCourseIsFixedRisk(false)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    !courseIsFixedRisk
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  סיכון באחוזים
                </button>
                <button
                  onClick={() => setCourseIsFixedRisk(true)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    courseIsFixedRisk
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  סיכון קבוע
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center">
                {courseIsFixedRisk 
                  ? 'סיכון קבוע: אותו סכום סיכון בכל עסקה - צמיחה לינארית'
                  : 'סיכון באחוזים: הסיכון גדל עם התיק - צמיחה מורכבת (ריבית דריבית)'}
              </p>
            </div>
          </div>

          {/* Trading Parameters */}
          <div className="bg-gray-800 p-4 rounded-xl mb-6">
            <h4 className="text-white font-bold mb-4 flex items-center gap-2">
              <TrendingUp size={20} />
              פרמטרי מסחר אחרי סיום הקורס
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {courseIsFixedRisk ? (
                <div>
                  <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                    <AlertTriangle size={16} />
                    סיכון קבוע ($)
                  </label>
                  <input
                    type="number"
                    value={courseFixedRiskAmount === '' ? '' : courseFixedRiskAmount}
                    onChange={(e) => handleNumberInput(e.target.value, setCourseFixedRiskAmount)}
                    className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
                  />
                </div>
              ) : (
                <div>
                  <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                    <AlertTriangle size={16} />
                    סיכון לעסקה (%)
                  </label>
                  <input
                    type="number"
                    value={courseRiskPercent === '' ? '' : courseRiskPercent}
                    onChange={(e) => handleNumberInput(e.target.value, setCourseRiskPercent)}
                    className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
                  />
                </div>
              )}

              <div>
                <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                  <BarChart3 size={16} />
                  יחס סיכון:תגמול (1:X)
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">1:</span>
                  <input
                    type="number"
                    value={courseRiskRewardRatio === '' ? '' : courseRiskRewardRatio}
                    onChange={(e) => handleNumberInput(e.target.value, setCourseRiskRewardRatio)}
                    className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                  <Activity size={16} />
                  WIN RATE (%)
                </label>
                <input
                  type="number"
                  value={courseWinRate === '' ? '' : courseWinRate}
                  onChange={(e) => handleNumberInput(e.target.value, setCourseWinRate)}
                  className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                  <Calculator size={16} />
                  עסקאות חודשיות
                </label>
                <input
                  type="number"
                  value={courseMonthlyTrades === '' ? '' : courseMonthlyTrades}
                  onChange={(e) => handleNumberInput(e.target.value, setCourseMonthlyTrades)}
                  className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Course Results */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-600 to-emerald-600 p-6 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/80 text-sm">החזר השקעה (ROI)</span>
                <TrendingUp className="text-white/60" size={24} />
              </div>
              <p className="text-3xl font-bold text-white">{courseROI.toFixed(0)}%</p>
              <p className="text-green-200 text-sm mt-2">
                על כל ההשקעה הכוללת
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-6 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/80 text-sm">נקודת איזון</span>
                <Target className="text-white/60" size={24} />
              </div>
              <p className="text-3xl font-bold text-white">
                {breakEvenMonth > 0 ? `חודש ${breakEvenMonth}` : 'לא חושב'}
              </p>
              <p className="text-blue-200 text-sm mt-2">
                מתי מחזירים את ההשקעה
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-pink-600 p-6 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/80 text-sm">רווח חודשי ממוצע</span>
                <DollarSign className="text-white/60" size={24} />
              </div>
              <p className="text-3xl font-bold text-white">${formatNumber(courseMonthlyProfit)}</p>
              <p className="text-purple-200 text-sm mt-2">
                מהחודש ה-{learningMonths + 1}
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-600 to-orange-600 p-6 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/80 text-sm">תיק סופי</span>
                <Wallet className="text-white/60" size={24} />
              </div>
              <p className="text-3xl font-bold text-white">${formatNumber(courseFinalPortfolio)}</p>
              <p className="text-yellow-200 text-sm mt-2">
                אחרי {courseYearsToCalculate} {courseYearsToCalculate === 1 ? 'שנה' : 'שנים'}
              </p>
            </div>
          </div>

          {/* Course Timeline Chart */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-white font-bold mb-4">התפתחות מעלות הקורס לרווחיות</h3>
            <div className="relative h-80">
              {courseChartData.length > 0 ? (
                <div className="h-full">
                  {/* Chart SVG */}
                  <svg className="w-full h-full" viewBox="0 0 800 300">
                    <defs>
                      <linearGradient id="courseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.1" />
                      </linearGradient>
                    </defs>
                    
                    {/* Grid lines */}
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <line
                        key={i}
                        x1="60"
                        y1={30 + i * 40}
                        x2="760"
                        y2={30 + i * 40}
                        stroke="#374151"
                        strokeDasharray="2,2"
                      />
                    ))}
                    
                    {/* Zero line */}
                    <line
                      x1="60"
                      y1={150}
                      x2="760"
                      y2={150}
                      stroke="#EF4444"
                      strokeWidth="2"
                    />
                    
                    {/* Chart path */}
                    {(() => {
                      const maxEarnings = Math.max(...courseChartData.map(d => d.earnings));
                      const minEarnings = Math.min(...courseChartData.map(d => d.earnings));
                      const range = maxEarnings - minEarnings || 1;
                      
                      return (
                        <path
                          d={`M 60 ${230 - ((courseChartData[0].earnings - minEarnings) / range) * 180} ${courseChartData.map((data, index) => 
                            `L ${60 + (index * 700 / (courseChartData.length - 1))} ${230 - ((data.earnings - minEarnings) / range) * 180}`
                          ).join(' ')}`}
                          fill="none"
                          stroke="#8B5CF6"
                          strokeWidth="3"
                        />
                      );
                    })()}
                    
                    {/* Learning phase marker */}
                    <rect 
                      x="60" 
                      y="20" 
                      width={Math.max(100, (learningMonths / courseChartData.length) * 700)} 
                      height="250" 
                      fill="#EC4899" 
                      fillOpacity="0.1" 
                    />
                    <text 
                      x={60 + Math.max(50, (learningMonths / courseChartData.length) * 350)} 
                      y="270" 
                      fill="#EC4899" 
                      fontSize="12" 
                      textAnchor="middle"
                    >
                      תקופת למידה ({learningMonths} חודשים)
                    </text>
                    
                    {/* Break even point */}
                    {breakEvenMonth > 0 && breakEvenMonth < courseChartData.length && (
                      <>
                        <circle
                          cx={60 + (breakEvenMonth * 700 / (courseChartData.length - 1))}
                          cy="150"
                          r="6"
                          fill="#10B981"
                        />
                        <text
                          x={60 + (breakEvenMonth * 700 / (courseChartData.length - 1))}
                          y="140"
                          fill="#10B981"
                          fontSize="11"
                          textAnchor="middle"
                        >
                          החזר השקעה
                        </text>
                      </>
                    )}
                    
                    {/* Y-axis labels */}
                    {(() => {
                      const maxEarnings = Math.max(...courseChartData.map(d => d.earnings));
                      const minEarnings = Math.min(...courseChartData.map(d => d.earnings));
                      
                      return (
                        <>
                          <text x="40" y="35" fill="#9CA3AF" fontSize="12" textAnchor="end">
                            ${formatNumber(maxEarnings)}
                          </text>
                          <text x="40" y="155" fill="#EF4444" fontSize="12" textAnchor="end">$0</text>
                          <text x="40" y="235" fill="#9CA3AF" fontSize="12" textAnchor="end">
                            -${formatNumber(Math.abs(minEarnings))}
                          </text>
                        </>
                      );
                    })()}
                  </svg>
                  
                  {/* Monthly details */}
                  {courseChartData.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-400 text-sm">רווח נקי כעת</p>
                        <p className={`text-lg font-bold ${totalEarnings >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ${formatNumber(totalEarnings)}
                        </p>
                      </div>
                      <div className="bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-400 text-sm">השקעה כוללת</p>
                        <p className="text-lg font-bold text-white">
                          ${formatNumber((coursePrice / exchangeRate) + courseInvestment)}
                        </p>
                      </div>
                      <div className="bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-400 text-sm">רווח חודשי נוכחי</p>
                        <p className="text-lg font-bold text-white">
                          ${courseChartData[courseChartData.length - 1] ? formatNumber(courseChartData[courseChartData.length - 1].monthlyProfit) : '0'}
                        </p>
                      </div>
                      <div className="bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-400 text-sm">גודל תיק נוכחי</p>
                        <p className="text-lg font-bold text-white">
                          ${courseChartData[courseChartData.length - 1] ? formatNumber(courseChartData[courseChartData.length - 1].portfolio) : '0'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">אין נתונים להצגה</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trading Tab (Original Content) */}
      {activeTab === 'trading' && (
        <div>
          {/* Account Type & Risk Type Toggles */}
          <div className="bg-gray-800 p-5 rounded-xl mb-6">
            <div className="space-y-3">
              {/* Account Type */}
              <div>
                <p className="text-sm text-gray-400 mb-2 text-center">סוג התיק</p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setIsFundedAccount(false)}
                    className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                      !isFundedAccount
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    תיק אישי
                  </button>
                  <button
                    onClick={() => setIsFundedAccount(true)}
                    className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                      isFundedAccount
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    תיק ממומן
                  </button>
                </div>
              </div>
              
              {/* Divider */}
              <div className="border-t border-gray-700"></div>
              
              {/* Risk Type */}
              <div>
                <p className="text-sm text-gray-400 mb-2 text-center">שיטת סיכון</p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => setIsFixedRisk(false)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      !isFixedRisk
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    סיכון באחוזים
                  </button>
                  <button
                    onClick={() => setIsFixedRisk(true)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isFixedRisk
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    סיכון קבוע
                  </button>
                </div>
                <p className="text-xs text-gray-500 text-center mt-2">
                  {isFixedRisk 
                    ? 'סיכון קבוע: אותו סכום סיכון בכל עסקה - צמיחה לינארית'
                    : 'סיכון באחוזים: הסיכון גדל עם התיק - צמיחה מורכבת (ריבית דריבית)'}
                </p>
              </div>
            </div>
            
            {/* FTMO Account Selection */}
            {isFundedAccount && (
              <div className="mt-4">
                <p className="text-sm text-gray-400 mb-3 text-center">בחר גודל חשבון FTMO:</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {Object.keys(ftmoAccounts).map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedFTMO(Number(size))}
                      className={`p-3 rounded-lg font-medium transition-all ${
                        selectedFTMO === Number(size)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      ${formatNumber(Number(size))}
                    </button>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">עלות מבחן:</p>
                      <p className="text-white font-bold">${ftmoAccounts[selectedFTMO].cost}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">סיכון יומי מקס':</p>
                      <p className="text-yellow-400 font-bold">${formatNumber(ftmoAccounts[selectedFTMO].dailyMax)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">סיכון כולל מקס':</p>
                      <p className="text-red-400 font-bold">${formatNumber(ftmoAccounts[selectedFTMO].totalMax)}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {!isFundedAccount && (
              <div className="bg-gray-800 p-4 rounded-xl">
                <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                  <DollarSign size={16} />
                  השקעה ראשונית ($)
                </label>
                <input
                  type="number"
                  value={investment === '' ? '' : investment}
                  onChange={(e) => handleNumberInput(e.target.value, setInvestment)}
                  className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
                />
              </div>
            )}

            {isFixedRisk ? (
              <div className="bg-gray-800 p-4 rounded-xl">
                <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                  <AlertTriangle size={16} />
                  סיכון קבוע ($)
                </label>
                <input
                  type="number"
                  value={fixedRiskAmount === '' ? '' : fixedRiskAmount}
                  onChange={(e) => handleNumberInput(e.target.value, setFixedRiskAmount)}
                  max={isFundedAccount ? ftmoAccounts[selectedFTMO].dailyMax : undefined}
                  className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
                />
                {isFundedAccount && (
                  <p className="text-xs text-yellow-400 mt-1">
                    מקסימום: ${formatNumber(ftmoAccounts[selectedFTMO].dailyMax)}
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-gray-800 p-4 rounded-xl">
                <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                  <AlertTriangle size={16} />
                  סיכון לעסקה (%)
                </label>
                <input
                  type="number"
                  value={riskPercent === '' ? '' : riskPercent}
                  onChange={(e) => handleNumberInput(e.target.value, setRiskPercent)}
                  max={isFundedAccount ? (ftmoAccounts[selectedFTMO].dailyMax / selectedFTMO) * 100 : 100}
                  className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
                />
                {isFundedAccount && (
                  <p className="text-xs text-yellow-400 mt-1">
                    מקסימום: {((ftmoAccounts[selectedFTMO].dailyMax / selectedFTMO) * 100).toFixed(1)}%
                  </p>
                )}
              </div>
            )}

            <div className="bg-gray-800 p-4 rounded-xl">
              <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                <BarChart3 size={16} />
                יחס סיכון:תגמול (1:X)
              </label>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">1:</span>
                <input
                  type="number"
                  value={riskRewardRatio === '' ? '' : riskRewardRatio}
                  onChange={(e) => handleNumberInput(e.target.value, setRiskRewardRatio)}
                  className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
                />
              </div>
            </div>

            <div className="bg-gray-800 p-4 rounded-xl">
              <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                <Activity size={16} />
                WIN RATE (%)
              </label>
              <input
                type="number"
                value={winRate === '' ? '' : winRate}
                onChange={(e) => handleNumberInput(e.target.value, setWinRate)}
                className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
              />
              <div className="mt-2 text-xs text-gray-500">
                {winningTrades.toFixed(1)} רווחיות | {losingTrades.toFixed(1)} הפסדיות
              </div>
            </div>

            <div className="bg-gray-800 p-4 rounded-xl">
              <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                <Calculator size={16} />
                עסקאות חודשיות
              </label>
              <input
                type="number"
                value={monthlyTrades === '' ? '' : monthlyTrades}
                onChange={(e) => handleNumberInput(e.target.value, setMonthlyTrades)}
                className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 outline-none"
              />
            </div>

            <div className="bg-gray-800 p-4 rounded-xl">
              <label className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                <Calendar size={16} />
                תקופת חישוב
              </label>
              <div className="grid grid-cols-3 gap-1">
                {[1, 2, 3].map((year) => (
                  <button
                    key={year}
                    onClick={() => setYearsToCalculate(year)}
                    className={`py-2 px-2 rounded-lg transition-all text-sm ${
                      yearsToCalculate === year
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {year === 1 ? 'שנה' : `${year} שנים`}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-1 mt-1">
                {[5, 7, 10].map((year) => (
                  <button
                    key={year}
                    onClick={() => setYearsToCalculate(year)}
                    className={`py-2 px-2 rounded-lg transition-all text-sm ${
                      yearsToCalculate === year
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {`${year} שנים`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-800 p-4 rounded-xl text-center border border-gray-700">
              <p className="text-gray-400 text-sm">יחידת סיכון</p>
              <p className="text-xl font-bold text-yellow-400">${formatNumber(riskAmountDollar)}</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-xl text-center border border-gray-700">
              <p className="text-gray-400 text-sm">רווח צפוי לעסקה</p>
              <p className="text-xl font-bold text-green-400">${formatNumber(profitPerTrade)}</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-xl text-center border border-gray-700">
              <p className="text-gray-400 text-sm">גודל תיק חודשי</p>
              <p className="text-xl font-bold text-blue-400">${formatNumber(monthlyPortfolio)}</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-xl text-center border border-gray-700">
              <p className="text-gray-400 text-sm">תיק אחרי {yearsToCalculate === 1 ? 'שנה' : `${yearsToCalculate} שנים`}</p>
              <p className="text-xl font-bold text-purple-400">${formatNumber(finalPortfolio)}</p>
            </div>
          </div>

          {/* Main Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-600 to-emerald-600 p-6 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/80 text-sm">רווח חודשי צפוי</span>
                <TrendingUp className="text-white/60" size={24} />
              </div>
              <p className="text-3xl font-bold text-white">${formatNumber(monthlyProfit)}</p>
              <p className="text-green-200 text-sm mt-2">ROI חודשי: {monthlyROI.toFixed(1)}%</p>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-6 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/80 text-sm">ROI שנתי</span>
                <BarChart3 className="text-white/60" size={24} />
              </div>
              <p className="text-3xl font-bold text-white">
                {yearlyROI >= 10000 ? formatNumber(yearlyROI) + '%' : yearlyROI.toFixed(1) + '%'}
              </p>
              <p className="text-blue-200 text-sm mt-2">
                {isFixedRisk ? 'צמיחה לינארית' : 'צמיחה מורכבת'}
              </p>
            </div>

            <div className="bg-gradient-to-br from-red-600 to-orange-600 p-6 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/80 text-sm">Max Drawdown</span>
                <TrendingDown className="text-white/60" size={24} />
              </div>
              <p className="text-3xl font-bold text-white">${formatNumber(maxDD)}</p>
              <p className="text-red-200 text-sm mt-2">{(investment || 0) > 0 ? (maxDD/((investment || 1))*100).toFixed(1) : 0}% מההון</p>
            </div>
          </div>

          {/* FTMO Warnings */}
          {isFundedAccount && maxDD > ftmoAccounts[selectedFTMO].totalMax && (
            <div className="bg-red-900/20 border border-red-600 rounded-lg p-4 mb-6">
              <p className="text-red-400 font-bold">
                ⚠️ אזהרה: Max Drawdown עולה על המגבלה של FTMO (${formatNumber(ftmoAccounts[selectedFTMO].totalMax)})
              </p>
              <p className="text-red-300 text-sm mt-1">
                עם הפרמטרים הנוכחיים, סיכוי גבוה להיפסל במבחן או לאבד את החשבון הממומן.
              </p>
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" dir="rtl">
            {/* Portfolio Growth Chart */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-white font-bold mb-4">
                צמיחת תיק השקעות {isFixedRisk ? '(לינארית)' : '(מורכבת)'}
              </h3>
              <div className="relative h-64">
                {chartData.length > 0 && Math.max(...chartData) > Math.min(...chartData) ? (
                  <svg className="w-full h-full" viewBox="0 0 400 250">
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.1" />
                      </linearGradient>
                    </defs>
                    
                    {/* Grid lines */}
                    {[0, 1, 2, 3, 4].map((i) => (
                      <line
                        key={i}
                        x1="40"
                        y1={50 + i * 40}
                        x2="380"
                        y2={50 + i * 40}
                        stroke="#374151"
                        strokeDasharray="2,2"
                      />
                    ))}
                    
                    {/* Chart line */}
                    <path
                      d={`M 40 ${210 - ((chartData[0] - Math.min(...chartData)) / (Math.max(...chartData) - Math.min(...chartData))) * 160} ${chartData.map((value, index) => 
                        `L ${40 + (index * 340 / (chartData.length - 1))} ${210 - ((value - Math.min(...chartData)) / (Math.max(...chartData) - Math.min(...chartData))) * 160}`
                      ).join(' ')}`}
                      fill="none"
                      stroke="#3B82F6"
                      strokeWidth="3"
                    />
                    
                    {/* Area under curve */}
                    <path
                      d={`M 40 210 L 40 ${210 - ((chartData[0] - Math.min(...chartData)) / (Math.max(...chartData) - Math.min(...chartData))) * 160} ${chartData.map((value, index) => 
                        `L ${40 + (index * 340 / (chartData.length - 1))} ${210 - ((value - Math.min(...chartData)) / (Math.max(...chartData) - Math.min(...chartData))) * 160}`
                      ).join(' ')} L 380 210 Z`}
                      fill="url(#gradient)"
                    />
                    
                    {/* Y-axis labels */}
                    <text x="20" y="55" fill="#9CA3AF" fontSize="12">${formatNumber(Math.max(...chartData))}</text>
                    <text x="20" y="135" fill="#9CA3AF" fontSize="12">${formatNumber((Math.max(...chartData) + Math.min(...chartData)) / 2)}</text>
                    <text x="20" y="215" fill="#9CA3AF" fontSize="12">${formatNumber(Math.min(...chartData))}</text>
                    
                    {/* X-axis labels */}
                    <text x="30" y="235" fill="#9CA3AF" fontSize="12">0</text>
                    <text x="190" y="235" fill="#9CA3AF" fontSize="12">{Math.floor(chartData.length / 2)}m</text>
                    <text x="360" y="235" fill="#9CA3AF" fontSize="12">{chartData.length - 1}m</text>
                  </svg>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">אין נתונים להצגה</p>
                  </div>
                )}
              </div>
            </div>

            {/* Win Rate Pie Chart */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-white font-bold mb-4">יחס הצלחה</h3>
              <div className="relative h-64 flex items-center justify-center">
                <div className="mr-6">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-gray-300">רווחיות: {winningTrades.toFixed(1)}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-gray-300">הפסדיות: {losingTrades.toFixed(1)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <span className="text-gray-300">סה"כ: {monthlyTrades || 0}</span>
                  </div>
                </div>
                <div className="relative">
                  <svg className="w-48 h-48" viewBox="0 0 100 100">
                    {/* Background circle */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#374151"
                      strokeWidth="20"
                    />
                    {/* Win rate arc */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#10B981"
                      strokeWidth="20"
                      strokeDasharray={`${(winRate || 0) * 2.51} 251`}
                      strokeDashoffset="0"
                      transform="rotate(-90 50 50)"
                    />
                    {/* Loss rate arc */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#EF4444"
                      strokeWidth="20"
                      strokeDasharray={`${(100 - (winRate || 0)) * 2.51} 251`}
                      strokeDashoffset={`-${(winRate || 0) * 2.51}`}
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-2xl font-bold text-white">{(winRate || 0).toFixed(1)}%</p>
                    <p className="text-sm text-gray-400">WIN RATE</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Period P&L Bar Chart */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-white font-bold mb-4">
                {yearsToCalculate <= 2 ? 'סימולציית רווח חודשי' :
                 yearsToCalculate === 3 ? 'סימולציית רווח רבעוני' :
                 'סימולציית רווח שנתי'}
              </h3>
              <div className="relative h-64 overflow-x-auto">
                {periodData.length > 0 ? (
                  <div className="flex items-end justify-start gap-2 h-full pb-6 min-w-max px-2">
                    {periodData.map((data, index) => {
                      const maxValue = Math.max(...periodData.map(d => Math.abs(d.value))) || 1;
                      const barHeight = (Math.abs(data.value) / maxValue) * 150;
                      
                      return (
                        <div key={index} className="flex flex-col items-center">
                          <div className="relative flex items-end h-48">
                            <button
                              onClick={() => data.type === 'month' && setSelectedMonthDetails(data)}
                              className={`${data.type === 'month' ? 'cursor-pointer hover:opacity-80' : ''} transition-all duration-300 rounded-t ${
                                data.positive 
                                  ? 'bg-gradient-to-t from-green-600 to-green-400' 
                                  : 'bg-gradient-to-b from-red-600 to-red-400'
                              }`}
                              style={{
                                width: '48px',
                                height: `${barHeight}px`,
                                marginTop: data.positive ? 'auto' : `${96 - barHeight}px`
                              }}
                            >
                              {data.type === 'month' && (
                                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                                  <ChevronRight className="text-white w-4 h-4" />
                                </div>
                              )}
                              <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-300 whitespace-nowrap">
                                ${Math.round(data.value)}
                              </span>
                            </button>
                          </div>
                          <p className="text-gray-400 text-xs mt-2 whitespace-nowrap">{data.period}</p>
                        </div>
                      );
                    })}
                    {/* Zero line */}
                    <div className="absolute w-full h-px bg-gray-600" style={{ bottom: '120px' }}></div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">אין נתונים להצגה</p>
                  </div>
                )}
              </div>
              {yearsToCalculate <= 2 && periodData.length > 0 && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  לחץ על עמודה כדי לראות פירוט עסקאות
                </p>
              )}
            </div>
          </div>

          {/* Trade Details Modal */}
          {selectedMonthDetails && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedMonthDetails(null)}>
              <div className="bg-gray-800 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-white">פירוט עסקאות - {selectedMonthDetails.period}</h3>
                  <button
                    onClick={() => setSelectedMonthDetails(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X size={24} />
                  </button>
                </div>
                
                <div className="mb-4">
                  <p className="text-gray-400 mb-2">סיכום חודשי:</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-700 p-3 rounded-lg">
                      <p className="text-gray-400 text-sm">סה"כ רווח/הפסד</p>
                      <p className={`text-xl font-bold ${selectedMonthDetails.positive ? 'text-green-400' : 'text-red-400'}`}>
                        ${formatNumber(selectedMonthDetails.value)}
                      </p>
                    </div>
                    <div className="bg-gray-700 p-3 rounded-lg">
                      <p className="text-gray-400 text-sm">עסקאות רווחיות</p>
                      <p className="text-xl font-bold text-green-400">
                        {selectedMonthDetails.trades.filter(t => t.type === 'win').length}
                      </p>
                    </div>
                    <div className="bg-gray-700 p-3 rounded-lg">
                      <p className="text-gray-400 text-sm">עסקאות מפסידות</p>
                      <p className="text-xl font-bold text-red-400">
                        {selectedMonthDetails.trades.filter(t => t.type === 'loss').length}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <p className="text-gray-400 mb-2">רשימת עסקאות:</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedMonthDetails.trades.map((trade) => (
                      <div key={trade.id} className="bg-gray-700 p-3 rounded-lg flex justify-between items-center">
                        <div>
                          <p className="text-white">עסקה #{trade.id}</p>
                          <p className="text-gray-400 text-sm">{trade.date}</p>
                        </div>
                        <div className={`text-lg font-bold ${trade.type === 'win' ? 'text-green-400' : 'text-red-400'}`}>
                          {trade.type === 'win' ? '+' : ''}{formatNumber(trade.amount)}$
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ROICalculator;